import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import { ScanLine } from 'lucide-react'
import { askPhoneToScan, phoneScans } from '../utils/phoneScan'

// Camera barcode scanner in a modal. Calls onScan(code) once with the first
// code read, then closes. Bluetooth/USB scanners don't need this: they type
// into whatever input has focus. Inside the phone app the phone's own
// scanner reads the code instead (utils/phoneScan.js). The modal goes on
// the page itself (document.body), so a scanner opened from a field's
// label or from a dialog shows over everything, and Escape closes only it.
export default function BarcodeScanner({ open, onClose, onScan }) {
  const regionId = 'barcode-scan-region'
  const scannerRef = useRef(null)
  const doneRef = useRef(false)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const [error, setError] = useState('')
  const [torch, setTorch] = useState({ supported: false, on: false })

  useEffect(() => {
    if (!open) return undefined
    if (phoneScans()) return askPhoneToScan((code) => (code ? onScan(code) : onClose()))
    // Before a dialog's own Escape (which would close the dialog under it).
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      closeRef.current?.()
    }
    window.addEventListener('keydown', onKey, true)
    doneRef.current = false
    setError('')
    setTorch({ supported: false, on: false })
    const scanner = new Html5Qrcode(regionId)
    scannerRef.current = scanner
    let cancelled = false

    const stop = async () => {
      try { await scanner.stop() } catch { /* not running */ }
      try { await scanner.clear() } catch { /* already cleared */ }
    }

    scanner.start(
      { facingMode: 'environment' },
      {
        fps: 12,
        // A wide, flat box suits UPC/EAN barcodes. Never under the
        // library's 50px (a scanner closed while starting measures 0).
        qrbox: (w, h) => {
          const width = Math.max(50, Math.min(320, Math.floor(Math.min(w, h) * 0.9)))
          return { width, height: Math.max(120, Math.floor(width * 0.35)) }
        },
        aspectRatio: 1.7777778,
        disableFlip: true,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        formatsToSupport: [
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
        ],
      },
      async (text) => {
        const code = String(text || '').trim()
        if (cancelled || doneRef.current || !code) return
        doneRef.current = true
        await stop()
        onScan(code)
      },
      () => {},
    )
      .then(async () => {
        // Closed while the camera was starting: turn it off now it's on.
        if (cancelled) { await stop(); return }
        try {
          const caps = await scanner.getRunningTrackCapabilities?.()
          setTorch({ supported: Boolean(caps?.torch), on: false })
        } catch { /* torch is optional */ }
      })
      .catch(() => { if (!cancelled) setError('The camera is blocked. Type the code, or use a handheld scanner.') })

    return () => {
      cancelled = true
      window.removeEventListener('keydown', onKey, true)
      stop()
      scannerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open || phoneScans()) return null

  const toggleTorch = async () => {
    try {
      await scannerRef.current?.applyVideoConstraints?.({ advanced: [{ torch: !torch.on }] })
      setTorch((t) => ({ ...t, on: !t.on }))
    } catch { /* some devices refuse */ }
  }

  return createPortal(
    <div className="scan-modal" onClick={onClose}>
      <div className="scan-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Scan a barcode">
        <div className="scan-head">
          <strong>Scan a barcode</strong>
          <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={onClose}>Close</button>
        </div>
        <div className="scan-body">
          <div id={regionId} className="scan-region" />
          <p className="sum-muted">Point the camera at the barcode.</p>
          {error && <p className="scan-error" role="alert">{error}</p>}
          {torch.supported && (
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={toggleTorch}>{torch.on ? 'Light off' : 'Light on'}</button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** A Scan button for a box that takes a barcode: it opens the scanner (the
    phone's own inside the app) and hands the code read to onScan. */
export function ScanButton({ onScan, disabled, className = 'ci-btn ci-btn-ghost ci-btn-sm', label = 'Scan' }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className={`${className} scan-btn`} onClick={() => setOpen(true)} disabled={disabled} title="Scan a barcode with the camera">
        <ScanLine size={15} aria-hidden="true" />{label}
      </button>
      <BarcodeScanner open={open} onClose={() => setOpen(false)} onScan={(code) => { setOpen(false); onScan(code) }} />
    </>
  )
}
